import { useState, useEffect } from "react";
import {
    db,
    collection,
    query,
    where,
    orderBy,
    limit,
    startAfter,
    doc,
    updateDoc
} from "../firebase";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function NewClientsScreen() {
    /* ---------- state ---------- */
    const [clients, setClients] = useState([]);
    const [initialClients, setInitialClients] = useState([]);
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [loading, setLoading] = useState(false);

    /* modal */
    const [showModal, setShowModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);

    /* ---------- initial load ---------- */
    useEffect(() => { fetchInitial(); }, []);

    /* -------- Firestore queries ------------------------------------ */
    const baseQuery = (extra = []) =>
        query(
            collection(db, "clients"),
            where("type", "==", "NUEVOS"),          // 👈 solo nuevos
            orderBy("createdAt", "desc"),
            ...extra
        );

    const fetchInitial = async () => {
        try {
            setLoading(true);
            const snap = await (await import("firebase/firestore")).getDocs(
                baseQuery([limit(30)])
            );
            if (!snap.empty) {
                const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setClients(docs);
                setInitialClients(docs);
                setLastDoc(snap.docs[snap.docs.length - 1]);
                if (snap.size < 30) setHasMore(false);
            } else { setHasMore(false); }
        } catch (e) {
            console.error(e); toast.error("Error al cargar nuevos clientes");
        } finally { setLoading(false); }
    };

    const fetchMore = async () => {
        if (!lastDoc) return;
        try {
            setLoading(true);
            const snap = await (await import("firebase/firestore")).getDocs(
                baseQuery([startAfter(lastDoc), limit(30)])
            );
            if (!snap.empty) {
                const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setClients(prev => [...prev, ...docs]);
                setInitialClients(prev => [...prev, ...docs]);
                setLastDoc(snap.docs[snap.docs.length - 1]);
                if (snap.size < 30) setHasMore(false);
            } else { setHasMore(false); }
        } catch (e) {
            console.error(e); toast.error("Error al cargar más");
        } finally { setLoading(false); }
    };

    /* -------- filtros ---------------------------------------------- */
    const filtered = clients.filter(c => {
        const razon = c.razonSocial?.toLowerCase() || "";
        const email = c.mail?.toLowerCase() || "";
        const cuit = c.cuit?.toLowerCase() || "";
        const search = searchTerm.toLowerCase();
        return razon.includes(search) || email.includes(search) || cuit.includes(search);
    });

    /* -------- modal ------------------------------------------------- */
    const openModal = cli => { setSelectedClient(cli); setShowModal(true); };
    const closeModal = () => { setSelectedClient(null); setShowModal(false); };

    /* -------- aceptar / rechazar ----------------------------------- */
    // Ajusta los valores de destino según tu lógica de negocio:
    const HANDLE = {
        aceptar: { field: "type", value: "ACTIVO" },
        rechazar: { field: "type", value: "RECHAZADO" }
    };

    const updateStatus = async action => {
        if (!selectedClient) return;
        const { field, value } = HANDLE[action];
        try {
            await updateDoc(doc(db, "clients", selectedClient.id), { [field]: value });
            toast.success(`Cliente marcado como ${value}`);

            // quítalo de la lista local (ya no es NUEVO)
            setClients(prev => prev.filter(c => c.id !== selectedClient.id));
            setInitialClients(prev => prev.filter(c => c.id !== selectedClient.id));
            closeModal();
        } catch (e) {
            console.error(e); toast.error("No se pudo actualizar");
        }
    };

    /* -------- render ------------------------------------------------ */
    return (
        <div className="p-6">
            <h1 className="text-3xl font-bold mb-4">Nuevos Clientes</h1>

            {/* búsqueda */}
            <div className="form-control mb-4">
                <label className="label"><span className="label-text font-bold">Buscar</span></label>
                <div className="flex items-center space-x-2">
                    <input
                        type="text"
                        className="input input-bordered w-full"
                        placeholder="Razón social, email o CUIT"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    <button className="btn bg-gray-400 text-white border-none" onClick={() => {
                        setSearchTerm(""); setClients(initialClients); setHasMore(true);
                    }}>Limpiar</button>
                </div>
            </div>

            {/* tabla */}
            <div className="bg-base-100 p-4 rounded shadow">
                <h2 className="text-lg font-bold mb-2">Pendientes de aprobación</h2>
                {filtered.length ? (
                    <table className="table w-full">
                        <thead>
                            <tr><th>Fecha</th><th>Razón Social</th><th>Nombre</th><th>Email</th><th>Celular</th><th>Provincia</th></tr>
                        </thead>
                        <tbody>
                            {filtered.map(c => (
                                <tr
                                    key={c.id}
                                    className="cursor-pointer hover:bg-base-200"
                                    onClick={() => openModal(c)}
                                >
                                    <td>{c.createdAt ? new Date(c.createdAt)
                                        .toLocaleDateString("es-AR") : "--"}</td>
                                    <td>{c.razonSocial || "--"} ({c.cuit || "--"})</td>
                                    <td>{c.firstName || "--"} {c.lastName || ""}</td>
                                    <td>{c.mail || "--"}</td>
                                    <td>{c.cellPhone || "--"}</td>
                                    <td>{c.province || "--"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : <p>No hay nuevos clientes.</p>}

                {hasMore && initialClients.length > 0 && (
                    <div className="flex justify-center mt-4">
                        <button
                            className="btn bg-[#e91c24] text-white border-none"
                            onClick={fetchMore}
                            disabled={loading}
                        >
                            {loading ? "Cargando..." : "Cargar más"}
                        </button>
                    </div>
                )}
            </div>

            {/* modal */}
            {showModal && selectedClient && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
                    <div className="bg-base-100 w-full max-w-lg p-6 rounded shadow-lg">
                        <h3 className="text-2xl font-bold mb-4">Revisión de cliente</h3>

                        <div className="space-y-1 text-sm">
                            <p><strong>CUIT:</strong> {selectedClient.cuit}</p>
                            <p><strong>Razón Social:</strong> {selectedClient.razonSocial}</p>
                            <p><strong>Nombre:</strong> {selectedClient.firstName} {selectedClient.lastName}</p>
                            <p><strong>Email:</strong> {selectedClient.mail}</p>
                            <p><strong>Celular:</strong> {selectedClient.phone || selectedClient.cellPhone || "--"}</p>

                            <p><strong>Provincia:</strong> {selectedClient.province}</p>
                        </div>

                        <div className="mt-6 flex gap-2">
                            <button
                                onClick={() => updateStatus("aceptar")}
                                className="btn bg-green-600 text-white border-none flex-1"
                            >Aceptar</button>
                            <button
                                onClick={() => updateStatus("rechazar")}
                                className="btn bg-red-600 text-white border-none flex-1"
                            >Rechazar</button>
                            <button
                                onClick={closeModal}
                                className="btn bg-gray-400 text-white border-none flex-1"
                            >Cerrar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
